import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Form } from 'react-bootstrap';
import { Typography } from '@mui/material';
import 'font-awesome/css/font-awesome.min.css';

const backgroundImagePath = process.env.PUBLIC_URL + "/bg-image.png";

function Home() {
  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const navigate = useNavigate();                           

  const routeMap = {
    SMAS: '/SilageMonitoringAlarmSystem',
    SMSF: '/SoilMoistureSensorFixedDepth',
    STAR: '/StartTopology',
    SMSV: '/SoilMoistureSensorVariableDepth',
    SAWS: '/SmartAutomaticWeatherStation',
  };

  const zoneInputs = new Set(['zone1', 'zone2', 'zone3', 'zone4']);

  const handleClick = async () => {
    const input = inputValue.trim();

    try {
      if (zoneInputs.has(input.toLowerCase())) {
        setErrorMessage('');
        navigate(`/getzonebaseddata?deviceId=${encodeURIComponent(input)}`);
        return;
      }

      const response = await fetch(`/api/moistureSensor/getDataByDeviceId?deviceId=${input}`);
      const data = await response.json();

      if (data.message === "Provided deviceId records not available") {
        setErrorMessage(data.message);
        return;
      }

      setErrorMessage('');
      const prefix = input.substring(0, 4).toUpperCase();
      const route = routeMap[prefix];

      if (route) {
        navigate(`${route}?deviceId=${encodeURIComponent(input)}`);
      } else {
        alert('Unknown device ID prefix!');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundImage: `url(${backgroundImagePath})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          zIndex: -1,
          transition: 'filter 0.4s ease-in-out',
          filter: isFocused ? 'blur(10px) brightness(0.5)' : 'blur(0px) brightness(1)',
        }}
      ></div>

      {/* Animation exclusively for the blinking "Enter Device ID" error message */}
      <style>
        {`
          @keyframes blinkErrorMessage {
            0% { opacity: 0.2; }
            50% { opacity: 1; text-shadow: 0 0 10px rgba(255, 255, 255, 1); }
            100% { opacity: 0.2; }
          }
          .blinking-error-msg {
            animation: blinkErrorMessage 1s infinite ease-in-out;
          }
          .custom-input::placeholder {
            color: rgba(255, 255, 255, 0.7);
            opacity: 1;
          }
        `}
      </style>

      <Container fluid className="p-0" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', zIndex: 1 }}>
        <Row className="w-100 flex-grow-1 m-0 justify-content-center">
          <div style={{ width: '100%', maxWidth: '420px', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            
            {/* Reduced size for Qualden Logo text format */}
            <Typography 
              variant="h5" 
              align="center" 
              style={{ 
                marginBottom: '8px', 
                fontWeight: 'bold', 
                color: '#ffffff', 
                textShadow: '0 2px 4px rgba(0,0,0,0.9)',
                letterSpacing: '1px'
              }}
            >
              Qualden
            </Typography>

            <Typography 
              variant="h4" 
              align="center" 
              style={{ 
                marginBottom: '35px', 
                fontWeight: 'bold', 
                color: '#ffffff', 
                textShadow: '0 3px 6px rgba(0,0,0,0.9)',
                transform: isFocused ? 'scale(1.05)' : 'scale(1)',
                transition: 'transform 0.3s ease'
              }}
            >
              Device ID Navigation
            </Typography>
            
            <Form className="w-100">
              <Form.Group>
                <Form.Label style={{ marginBottom: '8px', fontWeight: '600', color: '#ffffff', textShadow: '0 2px 4px rgba(0,0,0,0.9)', letterSpacing: '0.5px' }}>
                  Enter Device ID
                </Form.Label>
                <Form.Control
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Enter a Device ID..."
                  className="mb-4 custom-input"
                  style={{ 
                    padding: '15px', 
                    borderRadius: '8px', 
                    backgroundColor: 'transparent',
                    color: '#ffffff', 
                    border: isFocused ? '2px solid #007bff' : '1px solid rgba(255, 255, 255, 0.7)',
                    fontSize: '16px',
                    boxShadow: isFocused ? '0 0 15px rgba(0, 123, 255, 0.5)' : 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onKeyDown={handleKeyDown}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                />
              </Form.Group>
              
              <button 
                type="button"
                onClick={handleClick}
                className="w-100"
                style={{ 
                  padding: '15px', 
                  borderRadius: '8px', 
                  fontSize: '18px', 
                  fontWeight: '600',
                  backgroundColor: 'transparent', 
                  color: '#ffffff',
                  border: isFocused ? '2px solid #007bff' : '1px solid rgba(255, 255, 255, 0.7)',
                  cursor: 'pointer',
                  boxShadow: isFocused ? '0 0 20px rgba(0,123,255,0.8)' : 'none',
                  transition: 'all 0.3s ease'
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
              >
                Get Details
              </button>
              
              {/* Blinking white error message */}
              {errorMessage && (
                <p className="mt-3 text-center blinking-error-msg" style={{ color: '#ffffff', fontWeight: 'bold', textShadow: '0 2px 4px rgba(0,0,0,0.9)' }}>
                  {errorMessage}
                </p>
              )}
            </Form>

          </div>
        </Row>
      </Container>
    </div>
  );
}

export default Home;
